import { AbstractControl } from '@angular/forms';
import { isObservable } from 'rxjs';
import { FlowDefinition, FlowInstance } from '../models/flow.model';
import { FlowObjectDefinition, FlowObjectInstance, FlowObjectInstanceActor } from '../models/flow-object.model';
import { Const } from './const';
import { CookieService } from 'ngx-cookie-service';
import { Enums } from './enums';
import { EDocsService } from '../services/edocs.service';
import { ToastrService } from 'ngx-toastr';
import { DocumentLegalValueCombination } from '../models/edocs.model';
import { WritableSignal } from '@angular/core';

export const Utils = {
    getToastrErrorOptions(isQuick?: boolean, isLong?: boolean): any {
        return {
            closeButton: true,
            //disableTimeOut: true,
            timeOut: isQuick ? 5000 : (isLong ? 300000 : 60000),
            extendedTimeOut: isQuick ? 5000 : (isLong ? 300000 : 60000)
        };
    },

    scrollTo(id: string) {
        let interval = setInterval(() => {
            let elem = document.getElementById(id);
            if (elem.scrollHeight == 0) return;

            elem.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest'
            });

            clearInterval(interval);
        }, 300);
    },

    scrollToTop(selector?: string) {
        setTimeout(() => document.querySelector(selector || '.container > .content-wrapper').scroll({
            top: 0,
            left: 0,
            behavior: 'smooth'
        }), 400);
    },

    scrollToBottom(selector?: string) {
        setTimeout(() => document.querySelector(selector || '.container > .content-wrapper').scroll({
            top: window.innerHeight,
            left: 0,
            behavior: 'smooth'
        }), 400);
    },

    getParsedMessageHtml(
        cookieService: CookieService,
        message: string,
        forwardingProtocol: string,
        processProtocol: string
    ): string {
        let resolvedSubdomain = this.getResolvedEDocsSubdomain(cookieService);

        return message?.replaceAll(Const.TagPublicMessageForwardingProtocol, forwardingProtocol)
            .replaceAll(Const.TagPublicMessageForwardingLink, `https://${resolvedSubdomain}es.gov.br/e/${forwardingProtocol}`)
            .replaceAll(Const.TagPublicMessageProcessProtocol, processProtocol)
            .replaceAll(Const.TagPublicMessageProcessLink, `https://${resolvedSubdomain}es.gov.br/p/${processProtocol}`);
    },

    getResolvedEDocsSubdomain(cookieService: CookieService): string {
        const environment = atob(cookieService.get('prodest-eflow-env')).toLowerCase();
        let resolvedSubdomain = '{0}e-docs.{1}';

        switch (environment) {
            case 'loc':
            case 'des':
                resolvedSubdomain = resolvedSubdomain.replaceAll('{0}', '').replaceAll('{1}', 'dev.');
                break;

            case 'hom':
                resolvedSubdomain = resolvedSubdomain.replaceAll('{0}', '').replaceAll('{1}', 'hom.');
                break;

            case 'prd':
                resolvedSubdomain = resolvedSubdomain.replaceAll('{0}', '').replaceAll('{1}', '');
                break;

            case 'tre':
            default:
                resolvedSubdomain = resolvedSubdomain.replaceAll('{0}', 'treinamento.').replaceAll('{1}', '');
                break;
        }

        return resolvedSubdomain;
    },

    maskCnpj(cnpj: string): string {
        return cnpj.replace(/([0-9A-Za-z]{2})([0-9A-Za-z]{3})([0-9A-Za-z]{3})([0-9A-Za-z]{4})([0-9]{2})/, '$1.$2.$3/$4-$5');
    },

    isObject(x: any): boolean {
        return x != null && typeof x === 'object';
    },

    isFunction(value: any): boolean {
        return typeof value === 'function';
    },

    isString(x: any): boolean {
        return x != null && typeof x === 'string';
    },

    isNullOrEmpty(value: any): boolean {
        return value == null || value.toString().trim() === '';
    },

    isNotNullOrEmpty(value: any): boolean {
        return !Utils.isNullOrEmpty(value);
    },

    isStringEqual(str1: string, str2: string): boolean {
        return str1 != null
            && str2 != null
            && str1.toLowerCase().trim() === str2.toLowerCase().trim();
    },

    isNotStringEqual(str1: string, str2: string): boolean {
        return !Utils.isStringEqual(str1, str2);
    },

    clone(value: any): any {
        if (!this.isObject(value) || isObservable(value) || value.changingThisBreaksApplicationSecurity || ['RegExp', 'FileList', 'File', 'Blob'].includes(value.constructor.name)) {
            return value;
        }

        if (value._isAMomentObject && this.isFunction(value.clone)) {
            return value.clone();
        }

        if (value instanceof AbstractControl) {
            return null;
        }

        if (value instanceof Date) {
            return new Date(value.getTime());
        }

        if (Array.isArray(value)) {
            //return value.slice(0).map(v => this.clone(v));
            return null;
        }

        const proto = Object.getPrototypeOf(value);
        let c = Object.create(proto);
        c = Object.setPrototypeOf(c, proto);

        return Object.keys(value).reduce((newVal, prop) => {
            const propDesc = Object.getOwnPropertyDescriptor(value, prop);
            if (propDesc.get) {
                Object.defineProperty(newVal, prop, propDesc);
            } else {
                newVal[prop] = this.clone(value[prop]);
            }

            return newVal;
        }, c);
    },

    flowAreaReady(callback) {
        if (
            document.querySelector('.selected-flow-objects > .msg.empty') == null
            && document.querySelector('.selected-flow-objects .selected-flow-object') == null
        ) {
            setTimeout(() => this.flowAreaReady(callback), 300);
        } else {
            setTimeout(callback, 1);
        }
    },

    jsonSyntaxHighlight(json: string, keepHtmlTags?: boolean): string {
        if (!keepHtmlTags) {
            json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        }

        return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, match => {
            let cssClass = 'number';
            if (/^"/.test(match)) {
                if (/:$/.test(match)) {
                    cssClass = 'key';
                } else {
                    cssClass = 'string';
                }
            } else if (/true|false/.test(match)) {
                cssClass = 'boolean';
            } else if (/null/.test(match)) {
                cssClass = 'null';
            }
            return `<span class="${cssClass}">${match}</span>`;
        });
    },

    async canUseEDocsDocument(
        protocol: string,
        eDocsService: EDocsService,
        toastr: ToastrService
    ): Promise<{ id: string, canUse: boolean, hasErrors: boolean }> {
        if (Utils.isNullOrEmpty(protocol)) {
            return {
                id: null,
                canUse: false,
                hasErrors: true
            };
        }

        let response_Search = await eDocsService.postDocumentoSearch({ registros: [protocol] });

        if (!response_Search?.isSuccess) {
            toastr.error(`"${protocol}": ${response_Search.message.description}`, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return {
                id: null,
                canUse: false,
                hasErrors: true
            };
        }

        if (response_Search.data.result.length == 0) {
            toastr.warning(Enums.Messages.EDocsProtocolNotFound.replace('{0}', protocol), Enums.Messages.Warning, Utils.getToastrErrorOptions());
            return {
                id: null,
                canUse: false,
                hasErrors: true
            };
        }

        let response_PodeUsar = await eDocsService.getDocumentoPodeUsar(response_Search.data.result[0].id);

        if (!response_PodeUsar?.isSuccess) {
            toastr.error(`"${protocol}": ${response_PodeUsar.message.description}`, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return {
                id: null,
                canUse: false,
                hasErrors: true
            };
        }

        return {
            id: response_Search.data.result[0].id,
            canUse: response_PodeUsar.data,
            hasErrors: false
        };
    },

    async isValidEDocsDocument(
        data: any,
        formSchema: any,
        eDocsService: EDocsService,
        toastr: ToastrService
    ): Promise<boolean> {
        // schema dos componentes do form que necessitam de verificação
        let eDocsDocumentsComponents = formSchema.components.filter(x =>
            x.documentLegalValueCombination != null
            && ![
                DocumentLegalValueCombination.CidadaoDocumentoEletronicoAssinadoDigitalmente,
                DocumentLegalValueCombination.ServidorDocumentoEletronicoAssinadoDigitalmente
            ].includes(x.documentLegalValueCombination?.value)
        );
        let eDocsDocumentsComponentKeys = eDocsDocumentsComponents.map(x => ({
            key: x.key,
            documentLegalValueCombination: x.documentLegalValueCombination.value
        }));

        let isValid = true;

        for (let i = 0; i < eDocsDocumentsComponentKeys.length; i++) {
            let key = eDocsDocumentsComponentKeys[i].key;
            let documentLegalValueCombination = eDocsDocumentsComponentKeys[i].documentLegalValueCombination;
            let item = data[key];

            for (let j = 0; j < item?.fileName.length; j++) {
                // verifica se o documento é capturável no E-Docs
                let response = null;
                switch (documentLegalValueCombination) {
                    case DocumentLegalValueCombination.ServidorDocumentoEletronicoSemAssinatura:
                        response = await eDocsService.postDocumentoValidoNatoDigitalCopiaServidor(item.minioKey[j]);
                        break;

                    case DocumentLegalValueCombination.ServidorDocumentoEletronicoAssinadoEletronicamente:
                        response = await eDocsService.postDocumentoValidoNatoDigitalAutoAssinadoServidor(item.minioKey[j]);
                        break;

                    case DocumentLegalValueCombination.ServidorDocumentoEscaneadoDocumentoOriginal:
                    case DocumentLegalValueCombination.ServidorDocumentoEscaneadoCopiaSimples:
                    case DocumentLegalValueCombination.ServidorDocumentoEscaneadoCopiaAutenticadaCartorio:
                    case DocumentLegalValueCombination.ServidorDocumentoEscaneadoCopiaAutenticadaAdministrativamente:
                        response = await eDocsService.postDocumentoValidoDigitalizadoServidor(item.minioKey[j]);
                        break;

                    case DocumentLegalValueCombination.CidadaoDocumentoEletronicoSemAssinatura:
                        response = await eDocsService.postDocumentoValidoNatoDigitalCopiaCidadao(item.minioKey[j]);
                        break;

                    case DocumentLegalValueCombination.CidadaoDocumentoEletronicoAssinadoEletronicamente:
                        response = await eDocsService.postDocumentoValidoNatoDigitalAutoAssinadoCidadao(item.minioKey[j]);
                        break;

                    case DocumentLegalValueCombination.CidadaoDocumentoEscaneadoCopiaSimples:
                        response = await eDocsService.postDocumentoValidoDigitalizadoCidadao(item.minioKey[j]);
                        break;

                    default:
                        break;
                }

                if (!response?.isSuccess) {
                    toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return false;
                }

                // trata erros na validação dos documentos
                if (!response.data.isSuccess) {
                    let label = eDocsDocumentsComponents.find(x => x.key == key).label;
                    let index = item?.fileName.length > 1 ? ` (item ${j + 1})` : '';
                    toastr.warning(
                        Enums.Messages.InvalidEDocsDocument.replace('{0}', label + index).replace('{1}', response.data.message),
                        Enums.Messages.Pendency,
                        Utils.getToastrErrorOptions(false, true)
                    );
                    isValid = false;
                }
            }
        }

        return isValid;
    },

    async isValidIcpBrasil(
        data: any,
        formSchema: any,
        eDocsService: EDocsService,
        toastr: ToastrService
    ): Promise<boolean> {
        // schema dos componentes do form que necessitam de assinatura ICP-Brasil
        let icpBrasilComponents = formSchema.components.filter(x =>
            [
                DocumentLegalValueCombination.CidadaoDocumentoEletronicoAssinadoDigitalmente,
                DocumentLegalValueCombination.ServidorDocumentoEletronicoAssinadoDigitalmente
            ].includes(x.documentLegalValueCombination?.value)
        );
        let icpBrasilComponentKeys = icpBrasilComponents.map(x => x.key);

        let isValid = true;

        for (let i = 0; i < icpBrasilComponentKeys.length; i++) {
            let key = icpBrasilComponentKeys[i];
            let item = data[key];

            for (let j = 0; j < item?.fileName.length; j++) {
                // verifica se assinatura digital é válida
                const response = await eDocsService.postAssinaturaDigitalValida(item.minioKey[j]);

                if (!response.isSuccess) {
                    toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return false;
                }

                // trata erros na validação dos documentos
                if (!response.data.isSuccess) {
                    let label = icpBrasilComponents.find(x => x.key == key).label;
                    let index = item?.fileName.length > 1 ? ` (item ${j + 1})` : '';
                    toastr.warning(
                        Enums.Messages.InvalidIcpBrasil.replace('{0}', label + index),
                        Enums.Messages.Pendency,
                        Utils.getToastrErrorOptions(false, true)
                    );
                    isValid = false;
                }
            }
        }

        return isValid;
    },

    retypeCustomComponents(components: any[]) : any[] {
        components.filter(x => x.type == 'landlineNumber').forEach(x => x.type = 'phoneNumber');
        components.filter(x => x.type == 'cellNumber').forEach(x => x.type = 'phoneNumber');
        components.filter(x => x.type == 'cpf').forEach(x => x.type = 'textfield');
        components.filter(x => x.type == 'cnpj').forEach(x => x.type = 'textfield');
        components.filter(x => x.type == 'geoJson').forEach(x => x.type = 'textarea');
        components.filter(x => x.type == 'eDocsProtocol').forEach(x => x.type = 'textfield');
        components.filter(x => x.type == 'selectRepository').forEach(x => x.type = 'select');
        components.filter(x => x.type == 'selectFiltered').forEach(x => x.type = 'select');
        components.filter(x => x.type == 'tableCustom').forEach(x => x.type = 'table');

        return components;
    },

    decycleFlowDefinition(data: FlowDefinition): FlowDefinition {
        let dataClone = this.clone(data) as FlowDefinition;
        dataClone.flowObjectDefinitions = [];

        data.flowObjectDefinitions.forEach(flowObject => {
            let flowObjectClone = this.clone(flowObject) as FlowObjectDefinition;
            flowObjectClone.next = [];

            flowObject.next.forEach(next => {
                let nextClone = this.clone(next) as FlowObjectDefinition;
                flowObjectClone.next.push(nextClone);
            });

            dataClone.flowObjectDefinitions.push(flowObjectClone);
        });

        return dataClone;
    },

    decycleFlowInstance(data: FlowInstance): FlowInstance {
        let dataClone = this.clone(data) as FlowInstance;
        dataClone.flowObjectInstances = [];
        dataClone.flowObjectInstanceActors = [];

        data.flowObjectInstances.forEach(flowObject => {
            let flowObjectClone = this.clone(flowObject) as FlowObjectInstance;
            flowObjectClone.flowObjectInstanceActors = [];

            flowObject.flowObjectInstanceActors.forEach(actor => {
                let actorClone = this.clone(actor) as FlowObjectInstanceActor;
                flowObjectClone.flowObjectInstanceActors.push(actorClone);
            });

            dataClone.flowObjectInstances.push(flowObjectClone);
        });

        return dataClone;
    },

    decycleFlowObjectInstance(data: FlowObjectInstance): FlowObjectInstance {
        let dataClone = this.clone(data) as FlowObjectInstance;
        dataClone.flowObjectInstanceActors = [];

        data.flowObjectInstanceActors.forEach(actor => {
            let actorClone = this.clone(actor) as FlowObjectInstanceActor;
            dataClone.flowObjectInstanceActors.push(actorClone);
        });

        return dataClone;
    },

    updSignal($signal: WritableSignal<any>, callback: (x: any) => any) {
        $signal.update(callback);

        if (Array.isArray($signal())) {
            $signal.set([ ...$signal() ]);
        } else {
            $signal.set({ ...$signal() });
        }
    },

    setSignal($signal: WritableSignal<any>, value: any) {
        $signal.set(value);

        if (Array.isArray(value)) {
            $signal.set([ ...$signal() ]);
        } else {
            $signal.set({ ...$signal() });
        }
    },

    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    },

    isCpfValid(cpf: string) {
        cpf = cpf?.replace(/[^\d]/g, '');
        if (Utils.isNullOrEmpty(cpf)) return false;

        if (
            cpf.length != 11
            || cpf == '00000000000'
            || cpf == '11111111111'
            || cpf == '22222222222'
            || cpf == '33333333333'
            || cpf == '44444444444'
            || cpf == '55555555555'
            || cpf == '66666666666'
            || cpf == '77777777777'
            || cpf == '88888888888'
            || cpf == '99999999999'
        ) return false;

        let add = 0;
        for (let i = 0; i < 9; i++) {
            add += Number.parseInt(cpf.charAt(i)) * (10 - i);
        }

        let rev = 11 - (add % 11);
        if (rev == 10 || rev == 11) {
            rev = 0;
        }

        if (rev != Number.parseInt(cpf.charAt(9))) return false;

        add = 0;
        for (let i = 0; i < 10; i++) {
            add += Number.parseInt(cpf.charAt(i)) * (11 - i);
        }

        rev = 11 - (add % 11);
        if (rev == 10 || rev == 11) {
            rev = 0;
        }

        if (rev != Number.parseInt(cpf.charAt(10))) return false;

        return true;
    },

    isCnpjValid(cnpj: string) {
        let tamanhoCNPJSemDV = 12;
        let regexCNPJSemDV = /^([A-Z0-9]){12}$/;
        let regexCNPJ = /^([A-Z0-9]){12}([0-9]){2}$/;
        let cnpjZerado = '00000000000000';
        let pesosDV = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

        cnpj = cnpj?.replace(/[^0-9a-zA-Z]/g, '').toUpperCase();
        if (Utils.isNullOrEmpty(cnpj)) return false;
        if (cnpj.length !== 14 || [...cnpj].every(c => c === cnpj[0])) return false;

        const calculaDV = (cnpj) => {
            if (regexCNPJSemDV.test(cnpj) && cnpj !== cnpjZerado.substring(0, tamanhoCNPJSemDV)) {
                let somatorioDV1 = 0;
                let somatorioDV2 = 0;
                for (let i = 0; i < tamanhoCNPJSemDV; i++) {
                    const asciiDigito = cnpj.charCodeAt(i) - 48;
                    somatorioDV1 += asciiDigito * pesosDV[i + 1];
                    somatorioDV2 += asciiDigito * pesosDV[i];
                }

                const dv1 = somatorioDV1 % 11 < 2 ? 0 : 11 - (somatorioDV1 % 11);
                somatorioDV2 += dv1 * pesosDV[tamanhoCNPJSemDV];
                const dv2 = somatorioDV2 % 11 < 2 ? 0 : 11 - (somatorioDV2 % 11);

                return `${dv1}${dv2}`;
            }

            return false;
        };

        if (regexCNPJ.test(cnpj) && cnpj !== cnpjZerado) {
            const dvInformado = cnpj.substring(tamanhoCNPJSemDV);
            const dvCalculado = calculaDV(cnpj.substring(0, tamanhoCNPJSemDV));
            return dvInformado === dvCalculado;
        }

        return false;
    },

    isEmailValid(email: string) {
        if (Utils.isNullOrEmpty(email)) return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    },

    findDeep(data: any, checkFn: Function) {
        let found: any;

        // checa propriedades de qualquer nome em qualquer subnível do objeto inicial
        function traverse(item: any) {
            if (found != null) return;

            if (Array.isArray(item)) {
                for (const element of item) {
                    traverse(element);
                    if (found != null) return;
                }
            } else if (item != null && typeof item === 'object') {
                if (checkFn(item)) {
                    found = item;
                    return;
                }

                const children = Object.values(item);
                for (const child of children) {
                    traverse(child);
                    if (found != null) return;
                }
            }
        }

        traverse(data);
        return found;
    },

    filterDeep(data: any, checkFn: Function) {
        const results = [];

        // checa propriedades de qualquer nome em qualquer subnível do objeto inicial
        function traverse(item: any) {
            if (Array.isArray(item)) {
                item.forEach(traverse);
            } else if (item != null && typeof item === 'object') {
                if (checkFn(item)) {
                    results.push(item);
                }

                Object.values(item).forEach(traverse);
            }
        }

        traverse(data);
        return results;
    },

    updateDeep(data: any, checkFn: Function, updateFn: Function) {
        // checa propriedades de qualquer nome em qualquer subnível do objeto inicial
        function traverse(item: any) {
            if (Array.isArray(item)) {
                item.forEach(traverse);
            } else if (item != null && typeof item === 'object') {
                if (checkFn(item)) {
                    updateFn(item);
                }

                Object.values(item).forEach(traverse);
            }
        }

        traverse(data);
    },

    moveItemInPlace(arr: any[], fromIndex: number, toIndex: number) {
        const [movedItem] = arr.splice(fromIndex, 1);
        arr.splice(toIndex, 0, movedItem);
        return arr;
    },
};
